import { useState, type ReactNode } from 'react';
import type { PageExperience } from '@/features/experiences/types';
import { useAnalytics } from '@/hooks/useAnalytics';
import {
  Box,
  Button,
  Flex,
  Icon,
  Icons,
  Text,
  useDisclosure,
  useToast,
} from '@/ui';

import { SchoolReportModal } from './SchoolReportModal';
import { useSubmitSchoolFeedbackForm } from './useSubmitSchoolFeedbackForm';

type Props = {
  experience: PageExperience;
  // The last item on the feedback line, which wraps, so a caller can pin it to
  // the right end with an auto left margin.
  actions?: ReactNode;
};

export const EarlyHeroPrompt = ({ actions, experience }: Props) => {
  const { trackEvent } = useAnalytics();
  const { submitForm, isPending } = useSubmitSchoolFeedbackForm();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [confirmed, setConfirmed] = useState(false);

  const handleConfirm = () => {
    trackEvent('experience:click early-hero-confirm', {
      experienceId: experience.id,
    });

    submitForm(
      { formId: 'school-confirm', experienceId: experience.id },
      {
        onSuccess: () => {
          setConfirmed(true);
          toast.notify(
            'Thanks for confirming — you just helped every hero who opens this next!'
          );
        },
        onError: () => {
          toast.error('Error submitting, please try again');
        },
      }
    );
  };

  const handleReportOpen = () => {
    trackEvent('experience:click early-hero-report', {
      experienceId: experience.id,
    });
    onOpen();
  };

  return (
    <Box w="100%">
      <Flex align="center" gap="3" wrap="wrap">
        <Text color="gray.900" fontSize="sm" fontWeight="500">
          Send feedback:
        </Text>
        {confirmed ? (
          <Text color="gray.700" fontSize="sm" fontWeight="medium">
            ✓ Thanks for confirming!
          </Text>
        ) : (
          <Button
            color="gray.700"
            isLoading={isPending}
            leftIcon={<Icon as={Icons.ThumbsUp} boxSize={4} />}
            size="sm"
            variant="link"
            onClick={handleConfirm}
          >
            Looks good to me
          </Button>
        )}
        <Button
          color="gray.700"
          leftIcon={<Icon as={Icons.Pencil} boxSize={4} />}
          size="sm"
          variant="link"
          onClick={handleReportOpen}
        >
          Suggest an edit
        </Button>
        {actions}
      </Flex>

      <SchoolReportModal
        experience={experience}
        isOpen={isOpen}
        onClose={onClose}
      />
    </Box>
  );
};
